import React from "react";
import {
    Box,
    Container,
    Stack,
    Typography,
    Button,
    IconButton,
    Link as MuiLink,
} from "@mui/material";
import {
    FaWhatsapp,
    FaInstagram,
    FaEnvelope,
    FaArrowUpRightFromSquare,
} from "react-icons/fa6";

const WHATSAPP_PHONE = "5599999999999";
const INSTAGRAM_URL = "https://instagram.com/lscprodutora";
const EMAIL = "luscoprodutora@gmail.com";

function buildWhatsAppLink(message) {
    const text = encodeURIComponent(message || "");
    return `https://wa.me/${WHATSAPP_PHONE}?text=${text}`;
}

function FooterLink({ href, children, external = false }) {
    return (
        <MuiLink
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            underline="none"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                color: "rgba(255,255,255,0.76)",
                fontWeight: 700,
                fontSize: { xs: 14, md: 15 },
                letterSpacing: "0.02em",
                transition: "all .22s ease",
                "&:hover": {
                    color: "var(--verdeClaro)",
                    transform: "translateX(2px)",
                },
            }}
        >
            {children}
        </MuiLink>
    );
}

function FooterNavColumn({ title, links }) {
    return (
        <Stack spacing={1.4}>
            <Typography
                sx={{
                    color: "rgba(255,255,255,0.42)",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.16em",
                    fontWeight: 900,
                    mb: 0.8,
                }}
            >
                {title}
            </Typography>

            {links.map((item) => (
                <FooterLink
                    key={item.label}
                    href={item.href}
                    external={item.external}
                >
                    {item.label}
                </FooterLink>
            ))}
        </Stack>
    );
}

export default function Footer() {
    return (
        <Box
            component="footer"
            sx={{
                position: "relative",
                py: { xs: 5, md: 6 },
                bgcolor: "var(--preto)",
                overflow: "hidden",
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    mx: "auto",
                    px: { xs: 2, md: 3 },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: { xs: "28px", md: "38px" },
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: `
              radial-gradient(circle at top left, rgba(172,201,14,0.10), transparent 28%),
              radial-gradient(circle at bottom right, rgba(172,201,14,0.06), transparent 24%),
              linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)
            `,
                        boxShadow:
                            "0 30px 100px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
                    }}
                >
                    {/* Top shape */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: { xs: 180, sm: 260, md: 340 },
                            height: { xs: 36, md: 48 },
                            borderBottomLeftRadius: "999px",
                            borderBottomRightRadius: "999px",
                            background:
                                "linear-gradient(180deg, rgba(172,201,14,0.18), rgba(172,201,14,0.04))",
                            borderLeft: "1px solid rgba(255,255,255,0.06)",
                            borderRight: "1px solid rgba(255,255,255,0.06)",
                            borderBottom: "1px solid rgba(255,255,255,0.06)",
                            pointerEvents: "none",
                            opacity: 0.9,
                        }}
                    />

                    {/* Background technical art */}
                    <Box
                        component="svg"
                        viewBox="0 0 2200 1100"
                        preserveAspectRatio="xMidYMid slice"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0.22,
                            pointerEvents: "none",
                        }}
                    >
                        <defs>
                            <linearGradient id="footerStroke" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="rgba(172,201,14,0.22)" />
                                <stop offset="100%" stopColor="rgba(255,255,255,0.03)" />
                            </linearGradient>
                        </defs>

                        {/* LENS / DIAFRAGMA MAIS À DIREITA */}
                        <circle
                            cx="1520"
                            cy="520"
                            r="330"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1.1"
                        />
                        <circle
                            cx="1520"
                            cy="520"
                            r="255"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1"
                        />
                        <circle
                            cx="1520"
                            cy="520"
                            r="185"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1"
                        />
                        <circle
                            cx="1520"
                            cy="520"
                            r="108"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1"
                        />

                        {/* BLADES */}
                        <path
                            d="M1520 410 L1598 470 L1572 566 L1472 592 L1412 500 Z"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1"
                        />
                        <path
                            d="M1414 500 L1518 408 L1635 448 L1638 566 L1534 632 Z"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1"
                            opacity="0.82"
                        />
                        <path
                            d="M1534 632 L1638 566 L1690 672 L1620 776 L1498 738 Z"
                            fill="none"
                            stroke="url(#footerStroke)"
                            strokeWidth="1"
                            opacity="0.82"
                        />

                        {/* LINHAS ORGÂNICAS */}
                        <path
                            d="M-80 210 C 140 90, 320 280, 560 190 S 980 90, 1240 210 S 1710 320, 2280 170"
                            fill="none"
                            stroke="rgba(172,201,14,0.09)"
                            strokeWidth="1"
                        />
                        <path
                            d="M-100 420 C 120 330, 360 500, 640 400 S 1080 300, 1400 430 S 1800 540, 2280 410"
                            fill="none"
                            stroke="rgba(172,201,14,0.07)"
                            strokeWidth="1"
                        />
                        <path
                            d="M-120 760 C 140 620, 420 860, 720 740 S 1160 610, 1490 760 S 1870 900, 2300 760"
                            fill="none"
                            stroke="rgba(172,201,14,0.07)"
                            strokeWidth="1"
                        />

                        {/* DETALHES TÉCNICOS / CANTOS */}
                        <path d="M120 120 H 230 M120 120 V 230" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                        <path d="M2080 120 H 1970 M2080 120 V 230" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                        <path d="M120 980 H 230 M120 980 V 870" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                        <path d="M2080 980 H 1970 M2080 980 V 870" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />

                        {/* MICRO LINHAS EXTRAS */}
                        <path
                            d="M1260 300 L1820 300"
                            stroke="rgba(255,255,255,0.035)"
                            strokeWidth="1"
                        />
                        <path
                            d="M1260 742 L1820 742"
                            stroke="rgba(255,255,255,0.035)"
                            strokeWidth="1"
                        />
                        <path
                            d="M1245 315 L1245 725"
                            stroke="rgba(255,255,255,0.03)"
                            strokeWidth="1"
                        />
                        <path
                            d="M1835 315 L1835 725"
                            stroke="rgba(255,255,255,0.03)"
                            strokeWidth="1"
                        />
                    </Box>

                    {/* subtle grid */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: `
                linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
              `,
                            backgroundSize: "80px 80px",
                            maskImage:
                                "radial-gradient(circle at center, black 35%, transparent 100%)",
                            opacity: 0.18,
                            pointerEvents: "none",
                        }}
                    />

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            px: { xs: 2.2, sm: 3, md: 4, lg: 5 },
                            pt: { xs: 6, md: 7.5 },
                            pb: { xs: 2.5, md: 3 },
                        }}
                    >
                        {/* micro label */}
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            sx={{ mb: { xs: 3, md: 4 } }}
                        >
                            {["direção", "captação", "edição", "fotografia", "campanhas"].map(
                                (item) => (
                                    <Box
                                        key={item}
                                        sx={{
                                            px: 1.2,
                                            py: 0.7,
                                            borderRadius: 999,
                                            border: "1px solid rgba(255,255,255,0.08)",
                                            background: "rgba(255,255,255,0.03)",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "rgba(255,255,255,0.62)",
                                                fontSize: 11,
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.12em",
                                            }}
                                        >
                                            {item}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Stack>

                        {/* Main block */}
                        <Box
                            sx={{
                                display: "grid",
                                gap: { xs: 4, md: 5 },
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    lg: "1.35fr 0.65fr",
                                },
                                alignItems: "start",
                            }}
                        >
                            {/* Left */}
                            <Stack spacing={{ xs: 2.2, md: 3 }}>
                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.42)",
                                        fontSize: 12,
                                        fontWeight: 900,
                                        letterSpacing: "0.18em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    LSC PRODUTORA
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "var(--branco)",
                                        fontWeight: 1000,
                                        lineHeight: 0.94,
                                        letterSpacing: "-0.045em",
                                        fontSize: {
                                            xs: "clamp(1rem, 12vw, 2rem)",
                                            sm: "clamp(3.4rem, 9vw, 5.4rem)",
                                            md: "clamp(4.2rem, 7vw, 6.5rem)",
                                            lg: "clamp(1rem, 5vw, 4rem)",
                                        },
                                        maxWidth: { xs: "100%", lg: 920 },
                                    }}
                                >
                                    A LUZ COMEÇA AQUI.
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "block",
                                            color: "var(--verdeClaro)",
                                        }}
                                    >
                                        O IMPACTO FICA.
                                    </Box>
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.68)",
                                        lineHeight: 1.85,
                                        fontSize: { xs: 15, md: 17 },
                                        maxWidth: 720,
                                    }}
                                >
                                    Não criamos apenas peças visuais. Construímos presença,
                                    direção e linguagem para marcas, campanhas e experiências que
                                    precisam ser percebidas antes mesmo de serem explicadas.
                                </Typography>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    alignItems={{ sm: "center" }}
                                >
                                    <Button
                                        href={buildWhatsAppLink("Olá! Quero falar com a Lusco.")}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<FaWhatsapp />}
                                        variant="contained"
                                        sx={{
                                            minHeight: 58,
                                            px: 3.8,
                                            borderRadius: 999,
                                            bgcolor: "var(--verdeClaro)",
                                            color: "var(--preto)",
                                            fontWeight: 1000,
                                            boxShadow: "0 18px 36px rgba(172,201,14,0.18)",
                                            "&:hover": {
                                                bgcolor: "var(--verdeClaro)",
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 22px 42px rgba(172,201,14,0.22)",
                                            },
                                            width: { xs: "100%", sm: "fit-content" },
                                        }}
                                    >
                                        Iniciar briefing
                                    </Button>

                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.48)",
                                            fontSize: 12,
                                            lineHeight: 1.7,
                                            maxWidth: 320,
                                        }}
                                    >
                                        Atendimento para direção criativa, produção, campanhas,
                                        eventos e linguagem visual.
                                    </Typography>
                                </Stack>
                            </Stack>

                            {/* Right */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gap: { xs: 3, md: 4 },
                                    gridTemplateColumns: {
                                        xs: "1fr 1fr",
                                        sm: "repeat(3, 1fr)",
                                        lg: "1fr",
                                    },
                                    alignContent: "start",
                                }}
                            >
                                <FooterNavColumn
                                    title="navegação"
                                    links={[
                                        { label: "Sobre", href: "#sobre" },
                                        { label: "Números", href: "#numeros" },
                                        { label: "Vídeos", href: "#videos" },
                                        { label: "Fotos", href: "#fotos" },
                                        { label: "Equipe", href: "#equipe" },
                                        { label: "Contato", href: "#contato" },
                                    ]}
                                />

                                <FooterNavColumn
                                    title="contato"
                                    links={[
                                        {
                                            label: "WhatsApp",
                                            href: buildWhatsAppLink("Olá! Quero falar com a Lusco."),
                                            external: true,
                                        },
                                        {
                                            label: "Instagram",
                                            href: INSTAGRAM_URL,
                                            external: true,
                                        },
                                    ]}
                                />

                                <Stack spacing={1.4}>
                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.42)",
                                            fontSize: 11,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.16em",
                                            fontWeight: 900,
                                            mb: 0.8,
                                        }}
                                    >
                                        sinais
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "rgba(255,255,255,0.72)",
                                            fontSize: 14,
                                            lineHeight: 1.8,
                                        }}
                                    >
                                        light / motion / framing / presence / brand perception
                                    </Typography>

                                    <Stack direction="row" spacing={1}>
                                        <IconButton
                                            component="a"
                                            href={buildWhatsAppLink("Olá! Quero falar com a Lusco.")}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: "16px",
                                                color: "var(--branco)",
                                                border: "1px solid rgba(255,255,255,0.10)",
                                                background: "rgba(255,255,255,0.04)",
                                                "&:hover": {
                                                    borderColor: "rgba(172,201,14,0.28)",
                                                    background: "rgba(172,201,14,0.08)",
                                                },
                                            }}
                                        >
                                            <FaWhatsapp size={17} />
                                        </IconButton>

                                        <IconButton
                                            component="a"
                                            href={INSTAGRAM_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: "16px",
                                                color: "var(--branco)",
                                                border: "1px solid rgba(255,255,255,0.10)",
                                                background: "rgba(255,255,255,0.04)",
                                                "&:hover": {
                                                    borderColor: "rgba(172,201,14,0.28)",
                                                    background: "rgba(172,201,14,0.08)",
                                                },
                                            }}
                                        >
                                            <FaInstagram size={17} />
                                        </IconButton>

                                        <IconButton
                                            component="a"
                                            href={`mailto:${EMAIL}`}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: "16px",
                                                color: "var(--branco)",
                                                border: "1px solid rgba(255,255,255,0.10)",
                                                background: "rgba(255,255,255,0.04)",
                                                "&:hover": {
                                                    borderColor: "rgba(172,201,14,0.28)",
                                                    background: "rgba(172,201,14,0.08)",
                                                },
                                            }}
                                        >
                                            <FaEnvelope size={17} />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Box>

                        {/* divider */}
                        <Box
                            sx={{
                                mt: { xs: 4, md: 5 },
                                mb: { xs: 2.5, md: 2.8 },
                                height: 1,
                                background:
                                    "linear-gradient(90deg, rgba(255,255,255,0.04), rgba(172,201,14,0.18), rgba(255,255,255,0.04))",
                            }}
                        />

                        {/* Bottom */}
                        <Box
                            sx={{
                                display: "grid",
                                gap: 1.5,
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "1fr auto auto auto",
                                },
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "rgba(255,255,255,0.46)",
                                    fontSize: 12,
                                    lineHeight: 1.7,
                                }}
                            >
                                © {new Date().getFullYear()} LSC Produtora. Imagem, direção e
                                percepção visual com valor de marca.
                            </Typography>

                            <MuiLink
                                href="https://www.e2tcode.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    justifySelf: { xs: "start", md: "center" },
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.9,
                                    borderRadius: "999px",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    background: "rgba(255,255,255,0.03)",
                                    color: "rgba(255,255,255,0.68)",
                                    fontSize: 12,
                                    fontWeight: 800,
                                    letterSpacing: "0.04em",
                                    transition: "all .25s ease",
                                    "&:hover": {
                                        color: "#FF6A00",
                                        borderColor: "rgba(255,106,0,0.35)",
                                        background: "rgba(255,106,0,0.08)",
                                        boxShadow: "0 10px 24px rgba(255,106,0,0.14)",
                                        transform: "translateY(-1px)",
                                    },
                                }}
                            >
                                Desenvolvido por <Box component="span" sx={{ fontWeight: 1000 }}>E2T Code</Box>
                                <FaArrowUpRightFromSquare size={11} />
                            </MuiLink>

                            <FooterLink href="#top">Voltar ao topo</FooterLink>

                            <FooterLink
                                href={buildWhatsAppLink("Olá! Quero falar com a Lusco.")}
                                external
                            >
                                Falar agora <FaArrowUpRightFromSquare size={12} />
                            </FooterLink>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}